import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Copy, RefreshCw } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { smsApi } from "../../../api/resources";
import type { OutboxItem } from "../../../api/types";
import { PageHeader } from "../../../components/ComingSoon";
import { Button } from "../../../components/ui";
import { fmtDate } from "../../../utils/labels";
import { DataTable, LoadState, useLoad } from "../shared";
import { PreviewToolBadge } from "./smsUi";

const LINK_RE = /https?:\/\/\S+\/(?:p|c)\/[A-Za-z0-9_-]+/;

function LinkQr({ body, id }: { body: string | null; id: string }) {
  const link = body?.match(LINK_RE)?.[0];
  if (!link) return null;
  const copy = () => navigator.clipboard?.writeText(link).then(() => toast.success("Link copied"));
  return (
    <div data-testid={`outbox-link-${id}`} className="mt-3 flex items-center gap-4">
      <div className="rounded-sm bg-snow p-2" data-testid={`outbox-qr-${id}`}><QRCodeSVG value={link} size={112} /></div>
      <div className="space-y-2">
        <p className="text-[11px] text-zinc-500">Scan with a real phone to open the {link.includes("/p/") ? "GPS check" : "correction"} page.</p>
        <button type="button" onClick={copy} data-testid={`outbox-copy-link-${id}`} className="inline-flex items-center gap-1 rounded-sm border border-white/20 px-2 py-1 text-[11px] text-zinc-200 hover:border-white/50"><Copy className="h-3 w-3" /> Copy link</button>
        <a href={link} target="_blank" rel="noreferrer" data-testid={`outbox-open-link-${id}`} className="ml-2 text-[11px] text-signal hover:text-white">Open</a>
      </div>
    </div>
  );
}

export default function MockOutbox() {
  const [reload, setReload] = useState(0);
  const loader = useCallback(() => smsApi.outbox(), [reload]); // eslint-disable-line react-hooks/exhaustive-deps
  const { data, error } = useLoad<OutboxItem[]>(loader);
  return (
    <section data-testid="mock-outbox-page" className="animate-rise">
      <Link to="/admin/sms-activity" className="mb-4 inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-white" data-testid="mock-outbox-back-link"><ArrowLeft className="h-3.5 w-3.5" /> SMS Activity</Link>
      <PageHeader title="Mock SMS Outbox">
        <PreviewToolBadge testId="mock-outbox-preview-badge" />
        <Button variant="secondary" onClick={() => setReload((r) => r + 1)} data-testid="mock-outbox-refresh-button"><RefreshCw className="h-4 w-4" /> Refresh</Button>
      </PageHeader>
      <p className="mb-6 max-w-2xl text-sm text-zinc-400">No real texts are sent in Preview. Every outbound message and verification code lands here instead. This page and its API return 404 in production.</p>
      <LoadState error={error} loading={!data && !error} testId="mock-outbox-error" />
      {data && (
        <DataTable testId="mock-outbox-table" headers={["When", "To", "Type", "Code", "Message"]} empty={data.length === 0}>
          {data.map((m) => (
            <tr key={m.id} data-testid={`outbox-row-${m.id}`} className="align-top hover:bg-white/[0.03]">
              <td className="whitespace-nowrap px-5 py-3 text-xs text-zinc-400">{fmtDate(m.created_at)}</td>
              <td className="whitespace-nowrap px-5 py-3 font-mono text-xs text-zinc-200">{m.to_e164}</td>
              <td className="px-5 py-3 text-xs uppercase tracking-wider text-zinc-300">{m.kind === "VERIFY" ? "Verify code" : "Message"}</td>
              <td data-testid={`outbox-code-${m.id}`} className="px-5 py-3 font-mono text-lg font-bold tracking-[0.2em] text-signal">{m.code ?? ""}</td>
              <td className="max-w-lg px-5 py-3 text-xs text-zinc-300">{m.body}<LinkQr body={m.body} id={m.id} /></td>
            </tr>
          ))}
        </DataTable>
      )}
    </section>
  );
}
